import React, { useState, useEffect, useMemo, useCallback } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate } from "../utils/utils";
/* global gdAlertify */

// ============================================================================
// PROTOTIPO "oculto" de creación de patrones de rotación, pensado para un
// usuario administrativo (perfil +55, viene de Excel) en vez de un usuario
// técnico. Guarda/lee EXACTAMENTE contra los mismos endpoints y la misma
// forma de "configuracion" que ya usa PatronesRotacion.js, así que cualquier
// patrón creado aquí aparece y funciona igual en /patrones-rotacion y al
// "Aplicar" plantilla en la ficha de paciente. No hay cambios de backend.
// ============================================================================

const DIAS = [
    { value: 'Mon', label: 'Lun' },
    { value: 'Tue', label: 'Mar' },
    { value: 'Wed', label: 'Mié' },
    { value: 'Thu', label: 'Jue' },
    { value: 'Fri', label: 'Vie' },
    { value: 'Sat', label: 'Sáb' },
    { value: 'Sun', label: 'Dom' },
];

const DIA_INDICE = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; // getDay() -> code

const PRESETS_DIAS = [
    { id: 'lunvie', label: 'Lunes a Viernes', dias: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
    { id: 'todos', label: 'Todos los días', dias: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
    { id: 'finde', label: 'Fines de semana', dias: ['Sat', 'Sun'] },
    { id: 'ninguno', label: 'Limpiar', dias: [] },
];

// Un "slot" acá es un tramo de la secuencia rotativa: cuántos días dura y con
// qué período. NO dice qué auxiliar lo cubre — eso se decide después, en el
// paso "Asignar Slots" de Acuerdo Mutuo (que ya soporta 2, 3 o más auxiliares
// rotando sobre el mismo patrón). El patrón solo define la estructura.

const PERIODO_OPCIONES = [
    { value: 'day', label: 'Día', icon: 'bi-sun' },
    { value: 'night', label: 'Noche', icon: 'bi-moon-stars' },
    { value: 'full', label: 'Día y Noche', icon: 'bi-clock-history' },
];

const PERIODO_OPCIONES_SLOT = [
    ...PERIODO_OPCIONES,
    { value: 'libre', label: 'Libre / Descanso', icon: 'bi-moon' },
];

// ---------- Helper de días de servicio (misma idea que en NuevoAcuerdoMutuo2.js) ----------
const esDiaDeServicio = (fecha, diasServiceSet) => diasServiceSet.has(DIA_INDICE[fecha.getDay()]);

// Genera una previsualización de `numDias` días de calendario (incluye los no laborales,
// para que se vea el "hueco") a partir de un patrón fijo o rotativo ya armado.
function generarPreview({ tipo, serviceDays, fixedPeriod, phases }, numDias = 14) {
    const diasServiceSet = new Set(serviceDays.length > 0 ? serviceDays : DIAS.map(d => d.value));
    const hoy = new Date();
    hoy.setHours(12, 0, 0, 0);
    // Empezamos en el lunes de la semana actual para que el preview siempre se lea igual
    const diaSemanaHoy = hoy.getDay(); // 0=Dom
    const offsetHastaLunes = diaSemanaHoy === 0 ? -6 : 1 - diaSemanaHoy;
    const inicioPreview = new Date(hoy);
    inicioPreview.setDate(inicioPreview.getDate() + offsetHastaLunes);

    const dias = [];

    if (tipo === 'fixed') {
        for (let i = 0; i < numDias; i++) {
            const fecha = new Date(inicioPreview);
            fecha.setDate(fecha.getDate() + i);
            const laboral = esDiaDeServicio(fecha, diasServiceSet);
            dias.push({ fecha, periodo: laboral ? fixedPeriod : null, slot: null });
        }
        return dias;
    }

    // Rotativo: recorremos días calendario, pero solo los de servicio consumen cupo de slot
    const cicloCompleto = phases.reduce((sum, p) => sum + (parseInt(p.days) || 0), 0);
    if (cicloCompleto === 0) {
        for (let i = 0; i < numDias; i++) {
            const fecha = new Date(inicioPreview);
            fecha.setDate(fecha.getDate() + i);
            dias.push({ fecha, periodo: null, slot: null });
        }
        return dias;
    }

    let slotIdx = 0;
    let cupoRestante = parseInt(phases[0].days) || 1;

    for (let i = 0; i < numDias; i++) {
        const fecha = new Date(inicioPreview);
        fecha.setDate(fecha.getDate() + i);

        if (!esDiaDeServicio(fecha, diasServiceSet)) {
            dias.push({ fecha, periodo: null, slot: null });
            continue;
        }

        const slotActual = phases[slotIdx % phases.length];
        const esLibre = slotActual.period === 'mixto' && (slotActual.dailyPeriods || []).length > 0 && (slotActual.dailyPeriods || []).every(d => d === 'libre');
        dias.push({ fecha, periodo: esLibre ? 'libre' : slotActual.period, slot: (slotIdx % phases.length) + 1 });

        cupoRestante--;
        if (cupoRestante <= 0) {
            slotIdx++;
            cupoRestante = parseInt(phases[slotIdx % phases.length].days) || 1;
        }
    }
    return dias;
}

// ---------- Componente de días de servicio (grilla tipo Excel) ----------
function GrillaDias({ serviceDays, onToggle, onPreset }) {
    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {PRESETS_DIAS.map(p => (
                    <button
                        key={p.id}
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => onPreset(p.dias)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="d-flex gap-2 flex-wrap">
                {DIAS.map(d => {
                    const activo = serviceDays.includes(d.value);
                    return (
                        <button
                            key={d.value}
                            type="button"
                            onClick={() => onToggle(d.value)}
                            className={`btn ${activo ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ width: 64, height: 64, fontSize: '1rem', fontWeight: 600 }}
                        >
                            {d.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const OPCIONES_SEMANAS = [2, 3, 4, 6];

// ---------- Vista previa tipo calendario ----------
function VistaPrevia({ tipo, serviceDays, fixedPeriod, phases }) {
    const [semanas, setSemanas] = useState(2);
    const dias = useMemo(
        () => generarPreview({ tipo, serviceDays, fixedPeriod, phases }, semanas * 7),
        [tipo, serviceDays, fixedPeriod, phases, semanas]
    );

    const colorPara = (periodo) => {
        if (periodo === null) return { bg: '#f1f1f1', color: '#999', label: '—' };
        if (periodo === 'libre') return { bg: '#e9ecef', color: '#495057', label: 'Libre' };
        if (periodo === 'day') return { bg: '#fff3cd', color: '#664d03', label: 'Día' };
        if (periodo === 'night') return { bg: '#cfe2ff', color: '#084298', label: 'Noche' };
        if (periodo === 'full') return { bg: '#d1e7dd', color: '#0f5132', label: 'Día y Noche' };
        return { bg: '#f1f1f1', color: '#999', label: periodo };
    };

    const filas = [];
    for (let s = 0; s < semanas; s++) filas.push(dias.slice(s * 7, s * 7 + 7));
    const diaLabel = (f) => DIAS[(f.getDay() + 6) % 7].label; // Mon-first label

    const Fila = ({ semana }) => (
        <div className="d-flex gap-2 mb-2">
            {semana.map((d, i) => {
                const c = colorPara(d.periodo);
                return (
                    <div key={i} className="text-center" style={{ width: 78 }}>
                        <div className="small text-muted mb-1">{diaLabel(d.fecha)} {d.fecha.getDate()}</div>
                        <div
                            className="rounded d-flex flex-column align-items-center justify-content-center"
                            style={{ height: 56, background: c.bg, color: c.color, fontSize: '0.72rem', fontWeight: 600, border: '1px solid rgba(0,0,0,0.06)' }}
                        >
                            {d.slot !== null && <div style={{ fontSize: '0.65rem', opacity: 0.75 }}>Slot {d.slot}</div>}
                            <div>{c.label}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="p-3 border rounded bg-white">
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <div className="fw-bold"><i className="bi bi-eye me-2"></i>Así se vería ({semanas} semanas de ejemplo)</div>
                <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Ver:</span>
                    {OPCIONES_SEMANAS.map(n => (
                        <button
                            key={n}
                            type="button"
                            className={`btn btn-sm ${semanas === n ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setSemanas(n)}
                        >
                            {n} sem.
                        </button>
                    ))}
                </div>
            </div>
            {filas.map((semana, idx) => <Fila key={idx} semana={semana} />)}
            <div className="small text-muted mt-2">
                Las celdas grises con "—" son días que no están marcados como día de servicio (nadie trabaja ese día).
                El número "Slot N" es solo la posición dentro de la secuencia — a quién se le asigna cada slot se
                decide después, al crear el Acuerdo Mutuo con cada auxiliar.
            </div>
        </div>
    );
}

// ---------- Formulario principal ----------
function PatronBuilder({ patronEditando, onGuardado, onCancelarEdicion }) {
    const [titulo, setTitulo] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [cantidadAuxiliares, setCantidadAuxiliares] = useState(1);
    const [serviceDays, setServiceDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
    const [tipo, setTipo] = useState('fixed');
    const [fixedPeriod, setFixedPeriod] = useState('day');
    const [horaInicio, setHoraInicio] = useState('08:00');
    const [horaFin, setHoraFin] = useState('20:00');
    // slots: [{ period: 'day'|'night'|'full'|'libre'|'mixto', days: number, start, end, dailyPeriods?: [] }]
    // Cada slot es solo un tramo de la secuencia (duración + período + horario).
    // Quién (qué auxiliar) cubre cada slot NO se define acá, se define después en
    // Acuerdo Mutuo — ahí ya se soportan 2, 3 o más auxiliares rotando.
    const SLOT_DEFAULT = () => [
        { period: 'full', days: 2, start: '08:00', end: '20:00' },
        { period: 'libre', days: 2, start: '', end: '' },
    ];
    const [slots, setSlots] = useState(SLOT_DEFAULT());

    useEffect(() => {
        if (!patronEditando) return;
        setTitulo(patronEditando.titulo || "");
        setDescripcion(patronEditando.descripcion || "");
        setCantidadAuxiliares(patronEditando.cantidad_auxiliares || 1);
        setTipo(patronEditando.tipo || 'fixed');

        const config = typeof patronEditando.configuracion === 'string'
            ? JSON.parse(patronEditando.configuracion)
            : (patronEditando.configuracion || {});

        setServiceDays(Array.isArray(config.serviceDays) ? config.serviceDays : []);
        setHoraInicio(config.horaInicio || '08:00');
        setHoraFin(config.horaFin || '20:00');

        if (patronEditando.tipo === 'fixed') {
            setFixedPeriod(config.period || 'day');
        } else if (Array.isArray(config.phases) && config.phases.length >= 1) {
            setSlots(config.phases.map(p => {
                const esLibre = p.period === 'mixto' && (p.dailyPeriods || []).length > 0 && (p.dailyPeriods || []).every(d => d === 'libre');
                return {
                    period: esLibre ? 'libre' : p.period,
                    days: parseInt(p.days) || 1,
                    start: p.start || '',
                    end: p.end || '',
                    dailyPeriods: p.period === 'mixto' && !esLibre ? (p.dailyPeriods || []) : undefined,
                };
            }));
        }
    }, [patronEditando]);

    const aplicarPresetDias = (dias) => setServiceDays(dias);
    const toggleDia = (dia) => setServiceDays(prev => prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]);

    const addSlot = () => setSlots(prev => {
        const base = prev[0] || { start: '08:00', end: '20:00' };
        return [...prev, { period: 'full', days: 1, start: base.start || '08:00', end: base.end || '20:00' }];
    });
    const removeSlot = (idx) => setSlots(prev => prev.filter((_, i) => i !== idx));
    const updateSlotDays = (idx, days) => setSlots(prev => prev.map((s, i) => i === idx ? { ...s, days: Math.max(1, days) } : s));
    const updateSlotPeriod = (idx, period) => setSlots(prev => prev.map((s, i) => i === idx ? { ...s, period } : s));
    const updateSlotHora = (idx, campo, valor) => setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [campo]: valor } : s));

    // Slots derivados al formato que ya usa patternRows / template.configuracion.phases
    // en el resto del sistema (para no perder compatibilidad con lo existente).
    const phasesRotativo = useMemo(() => slots.map(s => {
        if (s.period === 'libre') {
            return { period: 'mixto', days: s.days, start: '', end: '', status: 'libre', dailyPeriods: Array.from({ length: s.days }, () => 'libre') };
        }
        if (s.period === 'mixto') {
            return { period: 'mixto', days: s.days, start: '', end: '', status: 'libre', dailyPeriods: s.dailyPeriods || [] };
        }
        return { period: s.period, days: s.days, start: s.start, end: s.end, status: 'libre', dailyPeriods: [] };
    }), [slots]);

    const resetForm = () => {
        setTitulo("");
        setDescripcion("");
        setCantidadAuxiliares(1);
        setServiceDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
        setTipo('fixed');
        setFixedPeriod('day');
        setHoraInicio('08:00');
        setHoraFin('20:00');
        setSlots(SLOT_DEFAULT());
    };

    const handleGuardar = async () => {
        if (!titulo.trim()) {
            gdAlertify.notiError("Ponle un nombre al patrón (ej: 'Lunes a viernes' o '2x2 día completo').");
            return;
        }
        if (serviceDays.length === 0) {
            gdAlertify.notiError("Marca al menos un día de servicio.");
            return;
        }
        if (tipo === 'rotative' && (slots.length === 0 || slots.some(s => !s.days || s.days <= 0))) {
            gdAlertify.notiError("Agrega al menos un slot con duración válida.");
            return;
        }

        const configuracion = {
            period: tipo === 'fixed' ? fixedPeriod : undefined,
            phases: tipo === 'rotative' ? phasesRotativo : [],
            horaInicio,
            horaFin,
            serviceDays,
        };

        const payload = {
            id: patronEditando?.id,
            titulo,
            descripcion,
            cantidad_auxiliares: cantidadAuxiliares,
            tipo,
            configuracion,
        };

        try {
            gdAlertify.showLoader();
            const response = await axiosInstance.post("/patrones-rotacion/guardar", payload);
            gdAlertify.hideLoader();
            if (!response.data.error) {
                gdAlertify.notiSuccess(response.data.message || "Patrón guardado");
                resetForm();
                onGuardado && onGuardado();
            } else {
                gdAlertify.notiError(response.data.message);
            }
        } catch (error) {
            gdAlertify.hideLoader();
            console.error("Error guardando patrón:", error);
            gdAlertify.notiError("Error al guardar el patrón");
        }
    };

    return (
        <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                    <i className="bi bi-magic me-2"></i>
                    {patronEditando ? `Editando: ${patronEditando.titulo}` : 'Crear patrón nuevo (versión simple)'}
                </h5>
            </div>
            <div className="card-body">
                <div className="row mb-4">
                    <div className="col-md-7 mb-3">
                        <label className="form-label fw-bold">¿Cómo se llama este patrón?</label>
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="Ej: Lunes a viernes de día, o 2x2 turno completo"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold">¿Cuántos auxiliares necesita?</label>
                        <input
                            type="number"
                            className="form-control form-control-lg"
                            min="1"
                            value={cantidadAuxiliares}
                            onChange={(e) => setCantidadAuxiliares(parseInt(e.target.value) || 1)}
                        />
                    </div>
                    <div className="col-md-2 mb-3 d-flex align-items-end">
                        {patronEditando && (
                            <button type="button" className="btn btn-outline-secondary w-100" onClick={onCancelarEdicion}>
                                Cancelar edición
                            </button>
                        )}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="form-label fw-bold fs-5">Paso 1 — ¿Qué días se trabaja?</label>
                    <GrillaDias serviceDays={serviceDays} onToggle={toggleDia} onPreset={aplicarPresetDias} />
                </div>

                <div className="mb-4">
                    <label className="form-label fw-bold fs-5">Paso 2 — ¿Es siempre igual, o rota?</label>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className={`btn btn-sm ${tipo === 'fixed' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setTipo('fixed')}
                        >
                            <i className="bi bi-calendar-check me-1"></i> Fijo (siempre los mismos días)
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${tipo === 'rotative' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setTipo('rotative')}
                        >
                            <i className="bi bi-arrow-repeat me-1"></i> Rotativo (secuencia de tramos)
                        </button>
                    </div>
                </div>

                {tipo === 'fixed' ? (
                    <div className="mb-4 p-3 border rounded bg-light">
                        <label className="form-label fw-bold">Paso 3 — ¿Turno de día, de noche, o ambos?</label>
                        <div className="d-flex gap-3 mb-3">
                            {PERIODO_OPCIONES.map(op => (
                                <button
                                    key={op.value}
                                    type="button"
                                    className={`btn ${fixedPeriod === op.value ? 'btn-primary' : 'btn-outline-secondary'} px-4 py-2`}
                                    onClick={() => setFixedPeriod(op.value)}
                                >
                                    <i className={`bi ${op.icon} me-2`}></i>{op.label}
                                </button>
                            ))}
                        </div>
                        <div className="row">
                            <div className="col-md-3">
                                <label className="form-label">Hora inicio</label>
                                <input type="time" className="form-control" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Hora fin</label>
                                <input type="time" className="form-control" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mb-4 p-3 border rounded bg-light">
                        <label className="form-label fw-bold">Paso 3 — Arma tu secuencia de slots</label>
                        <p className="small text-muted mb-2">
                            Cada fila es un tramo de la rotación. Define cuántos días dura, en qué período y su horario.
                        </p>

                        <div className="d-none d-md-flex align-items-center gap-2 px-2 mb-1 small text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                            <div style={{ width: 56 }}>Slot</div>
                            <div style={{ width: 84 }}>Duración</div>
                            <div style={{ width: 150 }}>Período</div>
                            <div style={{ width: 232 }}>Horario</div>
                        </div>

                        <div className="d-flex flex-column gap-2 mb-2">
                            {slots.map((slot, idx) => (
                                <div key={idx} className="d-flex align-items-center gap-2 py-2 px-2 bg-white border rounded flex-wrap">
                                    <div className="fw-bold text-primary small" style={{ width: 56 }}>Slot {idx + 1}</div>

                                    <div style={{ width: 84 }}>
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-control text-center"
                                                value={slot.days}
                                                onChange={(e) => updateSlotDays(idx, parseInt(e.target.value) || 1)}
                                                title="Duración en días"
                                            />
                                            <span className="input-group-text">d</span>
                                        </div>
                                    </div>

                                    {slot.period === 'mixto' ? (
                                        <span className="badge bg-secondary" style={{ width: 150 }}>Config. avanzada — no editable aquí</span>
                                    ) : (
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 150 }}
                                            value={slot.period}
                                            onChange={(e) => updateSlotPeriod(idx, e.target.value)}
                                        >
                                            {PERIODO_OPCIONES_SLOT.map(op => (
                                                <option key={op.value} value={op.value}>{op.label}</option>
                                            ))}
                                        </select>
                                    )}

                                    {slot.period !== 'libre' && slot.period !== 'mixto' ? (
                                        <div className="d-flex align-items-center gap-1">
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                style={{ width: 110 }}
                                                value={slot.start || ''}
                                                onChange={(e) => updateSlotHora(idx, 'start', e.target.value)}
                                                title="Hora inicio de este slot"
                                            />
                                            <span className="small text-muted">a</span>
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                style={{ width: 110 }}
                                                value={slot.end || ''}
                                                onChange={(e) => updateSlotHora(idx, 'end', e.target.value)}
                                                title="Hora fin de este slot"
                                            />
                                        </div>
                                    ) : (
                                        <div className="small text-muted fst-italic" style={{ width: 232 }}>Sin horario</div>
                                    )}

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger ms-auto"
                                        onClick={() => removeSlot(idx)}
                                        disabled={slots.length <= 1}
                                        title="Eliminar slot"
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={addSlot}>
                            <i className="bi bi-plus-circle me-1"></i>Agregar Slot
                        </button>
                    </div>
                )}

                <div className="mb-4">
                    <label className="form-label fw-bold fs-5">Vista previa</label>
                    <VistaPrevia tipo={tipo} serviceDays={serviceDays} fixedPeriod={fixedPeriod} phases={phasesRotativo} />
                </div>

                <div className="mb-3">
                    <label className="form-label">Notas (opcional)</label>
                    <textarea className="form-control" rows="2" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
                </div>

                <button type="button" className="btn btn-success btn-lg px-5" onClick={handleGuardar}>
                    <i className="bi bi-save me-2"></i>Guardar patrón
                </button>
            </div>
        </div>
    );
}

// ---------- Página completa: builder + lista de patrones guardados ----------
const PatronRotacionSimple = () => {
    const [patrones, setPatrones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [patronEditando, setPatronEditando] = useState(null);

    const fetchPatrones = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/patrones-rotacion/listar");
            if (!response.data.error) setPatrones(response.data.data || []);
        } catch (error) {
            console.error("Error cargando patrones:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPatrones(); }, [fetchPatrones]);

    const handleDelete = (id) => {
        gdAlertify.confirm({
            title: "Eliminar patrón",
            message: "¿Seguro que quieres eliminar este patrón?",
            onConfirm: async () => {
                try {
                    const response = await axiosInstance.post(`/patrones-rotacion/eliminar/${id}`);
                    if (!response.data.error) {
                        gdAlertify.notiSuccess(response.data.message);
                        fetchPatrones();
                    } else {
                        gdAlertify.notiError(response.data.message);
                    }
                } catch (error) {
                    console.error("Error eliminando patrón:", error);
                    gdAlertify.notiError("Error al eliminar el patrón");
                }
            }
        });
    };

    const columns = [
        { name: "id", title: "ID", width: 60 },
        { name: "titulo", title: "Título", width: 260 },
        { name: "tipo", title: "Tipo", width: 100, render: (row) => row.tipo === 'fixed' ? 'Fijo' : 'Rotativo' },
        { name: "cantidad_auxiliares", title: "Auxiliares", width: 100 },
        { name: "fecha_creacion", title: "Creado", width: 160, render: (row) => formatDate(row.fecha_creacion) },
        {
            name: "id", title: "Acciones", width: 150,
            render: (row) => (
                <div className="btn-group">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setPatronEditando(row)}>
                        <i className="bi bi-pencil-square"></i> Editar
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid mt-4 mb-5">
            <div className="alert alert-warning">
                <i className="bi bi-flask me-2"></i>
                <strong>Página de prueba (no listada en el menú).</strong> Usa exactamente el mismo almacenamiento
                que "Patrones de Rotación" — lo que crees aquí aparece allá y se puede aplicar en la ficha del
                paciente igual que cualquier plantilla existente.
            </div>

            <PatronBuilder
                patronEditando={patronEditando}
                onGuardado={() => { setPatronEditando(null); fetchPatrones(); }}
                onCancelarEdicion={() => setPatronEditando(null)}
            />

            <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 border-bottom">
                    <h5 className="mb-0 fw-bold">Patrones guardados</h5>
                </div>
                <div className="card-body">
                    <GdTable
                        columns={columns}
                        data={patrones}
                        defaultItemsPerPage={10}
                        itemsPerPageOptions={[10, 25, 50]}
                        initialSort={[0, "desc"]}
                        loading={loading}
                        exportFilename="patrones-rotacion-simple.xlsx"
                    />
                </div>
            </div>
        </div>
    );
};

export default PatronRotacionSimple;
